import {
  Box,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { knowledgebaseicon } from "assets";
import useTitle from "hooks/useTitle";
import { useState } from "react";
import {
  allCards,
  IKnowledgeSection,
  knowledgeSections,
  SectionGrid,
} from "./knowledge-base-sections";

// Nothing in this file needs changing to add a section — do that in
// ./knowledge-base-sections/index.ts.

const ALL_KEY = "all";

// "All" always sits on top of the sidebar and shows the cards of every section.
const sidebarSections: Array<Pick<IKnowledgeSection, "key" | "title" >> = [
  { key: ALL_KEY, title: "All" },
  ...knowledgeSections,
];

const KnowledgeBase = () => {
  useTitle("ATOM Pro | Knowledge Base");
  const [active, setActive] = useState(ALL_KEY);

  // Undefined while "All" is selected, which is what shows every section's cards.
  const activeSection = knowledgeSections.find((section) => section.key === active);

  const cards = activeSection
    ? activeSection.links.map((link) => ({
        ...link,
        label: activeSection.title,
        color: activeSection.color,
      }))
    : allCards;

  return (
    <Grid
      container
      direction="row"
      alignItems="flex-start"
      sx={{
        // The layout's <main> reserves 50px of bottom padding for the fixed
        // BottomAppbar. This page paints its own background down to the fold,
        // so pull that strip back instead of leaving it white.
        mb: "-50px",
      }}
    >
      <Grid
        item
        xs={12}
        sm={3}
        md={2}
        sx={{
          // Stays put while the cards column scrolls; the list scrolls inside
          // itself only when it is taller than the viewport. `top` matches the
          // layout's fixed AppBar so the first item never slides under it.
          position: "sticky",
          top: { xs: "56px", sm: "64px" },
          alignSelf: "flex-start",
          maxHeight: { xs: "calc(100vh - 56px)", sm: "calc(100vh - 64px)" },
          overflowY: "auto",
          // overflowY alone makes the browser treat overflow-x as auto too, and
          // the items' hover translate + shadows would show a stray scrollbar.
          overflowX: "hidden",
          px: 0.5,
        }}
      >
        <List>
          {sidebarSections.map((section) => {
            const isActive = section.key === active;
            return (
              <ListItemButton
                key={section.key}
                onClick={() => setActive(section.key)}
                sx={{
                  mb: 1.2,
                  px: 2,
                  py: 1.3,
                  borderRadius: "16px",
                  transition: "all 0.3s ease",
                  background: "rgba(255,255,255,0.6)",
                  backdropFilter: "blur(14px)",
                  WebkitBackdropFilter: "blur(14px)",
                  border: "1px solid rgba(255,255,255,0.7)",

                  boxShadow: `
                    0 4px 12px rgba(0,0,0,0.04),
                    inset 0 1px 1px rgba(255,255,255,0.8)
                  `,

                  ...(isActive && {
                    background: "rgba(59,130,246,0.15)",
                    border: "1px solid rgba(59,130,246,0.35)",
                    boxShadow: `
                      0 8px 20px rgba(59,130,246,0.25),
                      inset 0 1px 2px rgba(255,255,255,0.9)
                    `,
                  }),

                  "&:hover": {
                    background: "rgba(59,130,246,0.12)",
                    transform: "translateX(4px)",
                    boxShadow: `
                      0 8px 20px rgba(59,130,246,0.2),
                      inset 0 1px 2px rgba(255,255,255,0.9)
                    `,
                  },
                }}
              >
                {/* {section && (
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(255,255,255,0.7)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255,255,255,0.8)",

                        boxShadow: `
                          0 4px 10px rgba(0,0,0,0.05),
                          inset 0 1px 1px rgba(255,255,255,0.9)
                        `,
                      }}
                    >
                      {/* <img
                        src={section.icon}
                        alt={section.title}
                        style={{
                          width: 20,
                          height: 20,
                          objectFit: "contain",
                        }}
                      /> */}
                    {/* </Box>
                  </ListItemIcon>
                )} */} 

                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? "#1e40af" : "#334155",
                        letterSpacing: "0.3px",
                      }}
                    >
                      {section.title}
                    </Typography>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </Grid>
      <Grid item xs={12} sm={9} md={10}>
        <Box
          sx={{
            // Sits below the layout's fixed AppBar, so a plain 100vh would
            // push the page past the fold and expose the gap again.
            minHeight: { xs: "calc(100vh - 56px)", sm: "calc(100vh - 64px)" },
            px: { xs: 2, md: 3 },
            py: 3,
            background: "linear-gradient(135deg, #f4f6fb, #e8ecf7)",
          }}
        >
          {/* A section only supplies a Component if it wants a custom look;
              otherwise it gets the shared card grid. */}
          {activeSection?.Component ? (
            <activeSection.Component />
          ) : (
            // key={active} remounts the grid on section change, which clears
            // the search box it owns.
            <SectionGrid key={active} cards={cards} />
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default KnowledgeBase;
